/*
PURPOSE: Editorial recent-corals surface — a single HexBloom that rotates
through real recent contributions, with a link straight to the /trust
"where the money flows" transparency view.
USE CASES:
- Composed inside editorial page sections (Home2 How It Works, ForArtists
  strategy). Replaces the legacy CoralChart strip's visual with the bloom
  while keeping the same data source.
DOMAIN BOUNDARIES:
- Presentation only. Data comes from the shared useRecentContributions hook
  (single source of truth with the legacy RecentCoralVisualisation). No new
  fetch, no fabricated copy.
- The "view all" trigger links to the public /trust transparency section. There
  is no email gate: the same figures (and the raw CSV) are freely viewable there.
- Section heading and CTAs belong to the host editorial section, not here.
ASSUMPTIONS:
- Single-coral emphasis (per product direction "only one coral there"): one
  bloom is shown and rotates to another random recent coral every 5s, the
  cadence the legacy strip used.
- The bloom is HexBloom — a data-seeded mark, not a per-artist chart: its
  layout seeds off coral.transactionId and its cluster size from the artist
  count, so each coral renders a stable, distinct bloom across the rotation.
*/

import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { trackEvent, EventName } from 'shared/lib/eventTracking';
import HexBloom from '../HexBloom/HexBloom';
import { useRecentContributions } from '../../hooks/useRecentContributions';

// Verbatim from legacy CoralVisualisation so the recent-corals data block
// reads identically: strip a leading "The ", then ellipsis-truncate.
const truncateText = (text: string, maxLength: number): string => {
  if (text.startsWith('The ')) {
    text = text.slice(4);
  }
  if (text.length <= maxLength) return text;
  return text.slice(0, maxLength - 3) + '...';
};

interface RecentCoralsBloomProps {
  className?: string;
}

const RecentCoralsBloom: React.FC<RecentCoralsBloomProps> = ({ className }) => {
  const { coralData, isLoading, error } = useRecentContributions();
  const [currentIndex, setCurrentIndex] = useState<number | null>(null);

  // Single-coral emphasis: show one bloom, rotate to another random recent
  // coral every 5s (the legacy cadence; avoids an immediate repeat when more
  // than one coral is available).
  useEffect(() => {
    if (coralData.length === 0) {
      setCurrentIndex(null);
      return;
    }

    setCurrentIndex(Math.floor(Math.random() * coralData.length));

    if (coralData.length === 1) return;

    const interval = setInterval(() => {
      setCurrentIndex((prev) => {
        let next = Math.floor(Math.random() * coralData.length);
        if (prev !== null && next === prev) {
          next = (next + 1) % coralData.length;
        }
        return next;
      });
    }, 5000);

    return () => clearInterval(interval);
  }, [coralData]);

  if (isLoading) {
    return <div className="text-editorial-muted/70 font-cormorant text-base">Loading recent corals...</div>;
  }

  if (error) {
    return <div className="text-editorial-muted/70 font-cormorant text-base">{error}</div>;
  }

  if (coralData.length === 0 || currentIndex === null) {
    return null;
  }

  const coral = coralData[currentIndex];

  return (
    <div className={`flex flex-col items-center text-center ${className ?? ''}`}>
      <div className="flex flex-col sm:flex-row items-center gap-6 sm:gap-10">
        <HexBloom coral={coral} size={300} />
        <div className="text-left min-w-[12rem]">
          <p className="text-editorial-eyebrow uppercase text-editorial-muted/55">
            Monthly Contribution
          </p>
          <p className="font-cormorant text-editorial-muted text-3xl md:text-4xl tabular-nums mt-1">
            ${coral.totalAmount.toFixed(2)}
          </p>
          <div className="mt-4 space-y-1.5">
            {coral.artists.slice(0, 3).map((artist, index) => (
              <div
                key={index}
                className="flex justify-between items-center gap-4 text-sm text-editorial-muted/80"
              >
                <span className="overflow-hidden whitespace-nowrap text-ellipsis">
                  {truncateText(artist.name, 16)}
                </span>
                <span className="font-medium text-editorial-accent tabular-nums">
                  ${artist.amount.toFixed(2)}
                </span>
              </div>
            ))}
          </div>
        </div>
      </div>
      <p className="mt-6 text-editorial-muted/70 font-cormorant text-base">
        Real corals powering independent music.
      </p>
      <Link
        to="/trust#trust-financials"
        onClick={() => trackEvent(EventName.VIEW_ALL_CONTRIBUTIONS_INTERACTION, {})}
        className="mt-2 text-sm text-editorial-accent hover:underline focus:outline-none"
      >
        View all coral contributions, ever
      </Link>
    </div>
  );
};

export default RecentCoralsBloom;
